import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
    Modal,
    Button,
    Tag,
    Space,
    Typography,
    Alert,
} from "antd";
import {
    BarcodeOutlined,
    CalendarOutlined,
    DollarCircleOutlined,
    FileTextOutlined,
    LinkOutlined,
    ShopOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { IInvoiceWithFilters } from "../../../../services/http/services/InvoicesService";
import { EInvoiceStatus } from "../../../../enums";
import StatusTag from "../StatusTag";
import { formatDateStringToDateAndTime, convertMoedaBRDoJSNovo } from "../../../../utils/Conversores";
import { getLinkInvoice, normalizeLink, verifyLinkIsValid } from "../../../../utils/Functions";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text, Paragraph } = Typography;

interface ITableData extends IInvoiceWithFilters {
    rowKey: number | undefined;
    appUserName: string;
    appUserId: number;
    createdAt: Date;
}

interface IProps {
    open: boolean;
    onClose: () => void;
    invoice: ITableData | undefined;
}

export default function ModalInvoiceDetails({
    open,
    onClose,
    invoice,
}: IProps) {
    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    if (!invoice) return null;

    const ticketsCount = invoice.tickets ? invoice.tickets.length : 0;
    const cardBg = isDark ? "#1f1f1f" : "#f8fafc";
    const cardBorder = isDark ? "#303030" : "#e2e8f0";

    const typeColor =
        invoice.type === "NFC"
            ? "purple"
            : invoice.type === "NFE"
                ? "cyan"
                : "blue";

    const invoicePortalLink = invoice.type ? getLinkInvoice(invoice.type) : null;
    const rawQrLink = invoice.link ? normalizeLink(invoice.link).toString() : null;

    return (
        <Modal
            width={780}
            open={open}
            centered
            destroyOnClose
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FileTextOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                Nota Fiscal #{invoice.id}
                            </div>
                            <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 2 }}>
                                Detalhes e informações do documento fiscal
                            </div>
                        </div>
                    </Space>
                    <Space size={8}>
                        <Tag color={typeColor} style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, padding: "2px 8px" }}>
                            {invoice.type || "NFS"}
                        </Tag>
                        <StatusTag status={invoice.status} />
                    </Space>
                </div>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        {invoice.appUserId ? (
                            <Button
                                icon={<UserOutlined style={{ color: "#52c41a" }} />}
                                onClick={() => {
                                    onClose();
                                    navigate(`/usuarios/aplicativo/visualizar/${invoice.appUserId}`);
                                }}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontWeight: 500,
                                }}
                            >
                                Ver Perfil do Usuário
                            </Button>
                        ) : null}
                    </div>
                    <Button type="primary" onClick={onClose} style={{ minWidth: 90 }}>
                        Fechar
                    </Button>
                </div>
            }
            onCancel={onClose}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 0" }}>
                {invoice.message && (
                    <Alert
                        type={
                            invoice.status === EInvoiceStatus.error ? 'error'
                                : invoice.status === EInvoiceStatus.warning ? 'warning'
                                    : invoice.status === EInvoiceStatus.completed ? 'success'
                                        : 'info'
                        }
                        showIcon
                        message={
                            <span style={{ fontSize: 13, lineHeight: 1.4 }}>
                                <strong style={{ marginRight: 6 }}>Mensagem:</strong>
                                <span>{invoice.message}</span>
                            </span>
                        }
                        style={{
                            borderRadius: 8,
                            padding: "6px 12px",
                        }}
                    />
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 8,
                                background: isDark ? "rgba(82, 196, 26, 0.15)" : "#f6ffed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <DollarCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b", fontWeight: 500 }}>
                                Valor Total
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: isDark ? "#52c41a" : "#237804" }}>
                                R$ {convertMoedaBRDoJSNovo(invoice.value)}
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 8,
                                background: isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <BarcodeOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b", fontWeight: 500 }}>
                                Bilhetes Gerados
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: "#1677ff" }}>
                                {ticketsCount} {ticketsCount === 1 ? "Bilhete" : "Bilhetes"}
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 8,
                                background: isDark ? "rgba(114, 46, 209, 0.15)" : "#f9f0ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CalendarOutlined style={{ color: "#722ed1", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b", fontWeight: 500 }}>
                                Data de Importação
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                                {invoice.createdAt ? formatDateStringToDateAndTime(String(invoice.createdAt), true) : "--"}
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: 16,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", display: "flex", alignItems: "center", gap: 6 }}>
                            <ShopOutlined style={{ color: "#1677ff" }} />
                            <span>Estabelecimento & Documento</span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <div>
                                <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>Empresa / Emitente:</span>
                                <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                    {invoice.company || "--"}
                                </div>
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                <div>
                                    <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>Número da Nota:</span>
                                    <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                        {invoice.number || "--"}
                                    </div>
                                </div>

                                {invoice.authenticity_code ? (
                                    <div>
                                        <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>Cód. Autenticidade:</span>
                                        <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                            {invoice.authenticity_code}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            {(invoice.city_issue || invoice.state_issue) && (
                                <div>
                                    <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>Município / UF de Emissão:</span>
                                    <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                        {[invoice.city_issue, invoice.state_issue].filter(Boolean).join(" - ")}
                                    </div>
                                </div>
                            )}

                            {invoicePortalLink && (
                                <div style={{ marginTop: 4 }}>
                                    <a
                                        href={invoicePortalLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            fontSize: 12,
                                            color: "#1677ff",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <LinkOutlined />
                                        <span>Consultar no portal da nota</span>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: 16,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", display: "flex", alignItems: "center", gap: 6 }}>
                            <UserOutlined style={{ color: "#52c41a" }} />
                            <span>Cidadão / Usuário</span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <div>
                                <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>Nome no Cadastro:</span>
                                <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                    {invoice.appUserName || "--"}
                                </div>
                            </div>

                            {invoice.appUserId ? (
                                <div>
                                    <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>ID do Usuário:</span>
                                    <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                        #{invoice.appUserId}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
                {invoice.key ? (
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                Chave de Acesso (44 dígitos)
                            </span>
                        </div>
                        <Paragraph
                            copyable={{ text: String(invoice.key), tooltips: ["Copiar chave", "Copiado!"] }}
                            style={{
                                margin: 0,
                                fontFamily: "monospace",
                                fontSize: 12,
                                fontWeight: 600,
                                color: isDark ? "#60a5fa" : "#1d4ed8",
                                wordBreak: "break-all",
                            }}
                        >
                            {invoice.key}
                        </Paragraph>
                    </div>
                ) : null}
                {invoice.link && (
                    <div
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#8c8c8c" : "#64748b" }}>
                            Link do QR Code da Nota
                        </span>
                        {rawQrLink && verifyLinkIsValid({ link: invoice.link }) ? (
                            <a
                                href={rawQrLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontSize: 12,
                                    color: "#1677ff",
                                    fontWeight: 500,
                                    wordBreak: "break-all",
                                }}
                            >
                                <LinkOutlined />
                                <span>{invoice.link}</span>
                            </a>
                        ) : (
                            <Text style={{ fontSize: 12, wordBreak: "break-all", color: isDark ? "#a6a6a6" : "#64748b" }}>
                                {invoice.link}
                            </Text>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}
